import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransparentEconomics = () => {
  const [activeTab, setActiveTab] = useState('packages');

  const fastTrackPackages = [
    {
      name: 'LaunchPad Sprint',
      subtitle: 'Board-Ready Decision Package',
      duration: '4 Weeks',
      price: 'Fixed Rate',
      icon: 'Rocket',
      color: 'bg-blue-600',
      description: 'Complete Go/No-Go analysis with board-ready business case',
      deliverables: [
        'Market viability assessment',
        'Total cost of ownership model',
        'Risk evaluation framework',
        'Talent availability analysis',
        'Location optimization study',
        'Board presentation deck'
      ],
      idealFor: 'CEOs evaluating GCC feasibility',
      guarantee: '100% refund if not board-ready'
    },
    {
      name: 'Rapid Setup',
      subtitle: 'Operational in 90 Days',
      duration: '90 Days',
      price: 'Transparent All-In',
      icon: 'Zap',
      color: 'bg-green-600',
      description: 'Complete GCC setup from entity formation to first team operational',
      deliverables: [
        'Legal entity + banking (6 weeks)',
        'First 5 leaders hired',
        '100 pre-screened CVs',
        'Zero-trust IT infrastructure',
        'Office setup + compliance',
        'Executive cockpit dashboard'
      ],
      idealFor: 'Fast-moving enterprises',
      guarantee: 'Operational guarantee or fee waiver'
    },
    {
      name: 'Build-Operate-Transfer',
      subtitle: 'Full Lifecycle Partnership',
      duration: '12-18 Months',
      price: 'Phased Investment',
      icon: 'Building',
      color: 'bg-purple-600',
      description: 'Complete setup, operational management, and ownership transfer',
      deliverables: [
        'Complete GCC establishment',
        'Team scaling to 100+',
        'Process optimization',
        'Knowledge transfer program',
        'Ownership transition',
        'Post-transfer support'
      ],
      idealFor: 'Strategic long-term expansion',
      guarantee: 'Transfer readiness guarantee'
    }
  ];

  const costComparison = {
    categories: [
      {
        name: 'Setup Costs',
        bootminds: '$75K-150K',
        diy: '$200K-500K',
        consultants: '$150K-300K',
        savings: '50-70%'
      },
      {
        name: 'Timeline',
        bootminds: '90 days',
        diy: '18-24 months',
        consultants: '12-18 months',
        savings: '75% faster'
      },
      {
        name: 'Hidden Costs',
        bootminds: '$0',
        diy: '$50K-200K',
        consultants: '$30K-100K',
        savings: '100% eliminated'
      },
      {
        name: 'Compliance Risk',
        bootminds: '0% (guaranteed)',
        diy: 'High',
        consultants: 'Medium',
        savings: 'Zero risk'
      },
      {
        name: 'Talent Access',
        bootminds: '100 CVs in 8 weeks',
        diy: '6-12 months',
        consultants: '3-6 months',
        savings: '4-6x faster'
      }
    ]
  };

  const hiddenCostEliminator = [
    {
      category: 'Legal & Compliance',
      hiddenCosts: [
        'Unexpected regulatory filings',
        'Legal structure modifications',
        'Compliance penalties',
        'Director liability issues'
      ],
      bootmindsApproach: 'Fixed-rate Entity-in-a-Box™ with compliance guarantee',
      savingsRange: '$30K-80K'
    },
    {
      category: 'Talent Acquisition',
      hiddenCosts: [
        'Failed recruitment fees',
        'Extended vacancy costs',
        'Incorrect compensation benchmarks',
        'Attrition replacement costs'
      ],
      bootmindsApproach: 'Performance-based pricing with retention guarantee',
      savingsRange: '$50K-150K'
    },
    {
      category: 'Infrastructure',
      hiddenCosts: [
        'IT setup delays',
        'Security incidents',
        'Office lease penalties',
        'Vendor management overhead'
      ],
      bootmindsApproach: 'Turnkey infrastructure with SLA guarantees',
      savingsRange: '$40K-100K'
    },
    {
      category: 'Operational',
      hiddenCosts: [
        'Process inefficiencies',
        'Quality issues',
        'Knowledge gaps',
        'Management overhead'
      ],
      bootmindsApproach: 'Managed operations with performance metrics',
      savingsRange: '$60K-200K'
    }
  ];

  const pricingPrinciples = [
    {
      icon: 'Eye',
      title: 'Transparent Pricing',
      description: 'Every cost itemized upfront. No hidden fees, no surprises.'
    },
    {
      icon: 'Shield',
      title: 'Fixed-Rate Options',
      description: 'Many services offered at fixed rates for budget predictability.'
    },
    {
      icon: 'TrendingUp',
      title: 'Performance-Based',
      description: 'Align our fees with your success metrics and outcomes.'
    },
    {
      icon: 'DollarSign',
      title: 'Pass-Through Costs',
      description: 'Government fees and licenses at actual cost, zero markup.'
    }
  ];

  return (
    <section id="transparent-pricing" className="py-20 bg-gradient-to-br from-background to-muted/30">
      <div className="container-executive">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-green-600/10 text-green-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="DollarSign" size={16} />
            <span>CFO-Approved Economics</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-headline font-bold text-text-primary mb-6">
            Transparent Economics,
            <span className="block text-accent">Predictable ROI</span>
          </h2>

          <p className="text-xl text-text-secondary">
            No hidden costs. No surprises. Just clear, transparent pricing with guaranteed outcomes.
          </p>
        </div>

        {/* Pricing Principles */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {pricingPrinciples.map((principle, index) => (
            <div key={index} className="card-elevated rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-green-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={principle.icon} size={24} color="white" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">{principle.title}</h3>
              <p className="text-sm text-text-secondary">{principle.description}</p>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {['packages', 'comparison', 'hidden-costs'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === tab
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-card text-text-secondary hover:bg-muted'
              }`}
            >
              {tab === 'packages' && 'Fast-Track Packages'}
              {tab === 'comparison' && 'Cost Comparison'}
              {tab === 'hidden-costs' && 'Hidden Cost Eliminator'}
            </button>
          ))}
        </div>

        {/* Fast-Track Packages */}
        {activeTab === 'packages' && (
          <div className="grid lg:grid-cols-3 gap-8">
            {fastTrackPackages.map((pkg, index) => (
              <div
                key={index}
                className={`card-elevated rounded-2xl p-8 relative overflow-hidden ${
                  index === 1 ? 'ring-2 ring-accent scale-105 lg:scale-110' : ''
                }`}
              >
                {index === 1 && (
                  <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-bold">
                    MOST POPULAR
                  </div>
                )}

                <div className={`w-16 h-16 ${pkg.color} rounded-xl flex items-center justify-center mb-6`}>
                  <Icon name={pkg.icon} size={32} color="white" />
                </div>

                <h3 className="text-2xl font-headline font-bold text-text-primary mb-2">
                  {pkg.name}
                </h3>
                <p className="text-text-secondary mb-4">{pkg.subtitle}</p>

                <div className="flex items-baseline justify-between mb-6 pb-6 border-b border-border">
                  <div>
                    <div className="text-3xl font-bold text-text-primary">{pkg.price}</div>
                    <div className="text-sm text-text-secondary mt-1">{pkg.duration}</div>
                  </div>
                  <Button
                    variant={index === 1 ? 'default' : 'outline'}
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Get Quote
                  </Button>
                </div>

                <p className="text-text-secondary mb-6">{pkg.description}</p>

                <div className="mb-6">
                  <h4 className="font-semibold text-text-primary mb-3">Key Deliverables:</h4>
                  <ul className="space-y-2">
                    {pkg.deliverables.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                        <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-muted rounded-lg p-4 mb-4">
                  <div className="text-sm font-medium text-text-primary mb-1">Ideal For:</div>
                  <div className="text-sm text-text-secondary">{pkg.idealFor}</div>
                </div>

                <div className="bg-green-600/10 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <Icon name="Shield" size={16} className="text-green-600 mt-0.5" />
                    <div className="text-sm text-green-600 font-medium">{pkg.guarantee}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Cost Comparison */}
        {activeTab === 'comparison' && (
          <div className="card-elevated rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                      Category
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-primary">
                      <div className="flex items-center justify-center space-x-2">
                        <Icon name="Award" size={16} />
                        <span>Bootminds</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-text-secondary">
                      DIY Approach
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-text-secondary">
                      Traditional Consultants
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-success">
                      Your Savings
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {costComparison.categories.map((row, index) => (
                    <tr key={index} className="hover:bg-muted/50">
                      <td className="px-6 py-4 font-medium text-text-primary">{row.name}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="font-semibold text-primary">{row.bootminds}</div>
                      </td>
                      <td className="px-6 py-4 text-center text-text-secondary">{row.diy}</td>
                      <td className="px-6 py-4 text-center text-text-secondary">{row.consultants}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center space-x-1 text-success font-semibold">
                          <Icon name="TrendingDown" size={16} />
                          <span>{row.savings}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-gradient-to-r from-green-600/10 to-blue-600/10 p-6 text-center">
              <p className="text-text-primary font-semibold mb-2">
                Cost-savings ranges available on request, sized to your scope and headcount
              </p>
              <p className="text-text-secondary text-sm">
                Plus 75% faster time to market and zero compliance risk
              </p>
            </div>
          </div>
        )}

        {/* Hidden Cost Eliminator */}
        {activeTab === 'hidden-costs' && (
          <div className="space-y-6">
            {hiddenCostEliminator.map((item, index) => (
              <div key={index} className="card-elevated rounded-xl p-8">
                <div className="flex items-start justify-between mb-6">
                  <h3 className="text-2xl font-headline font-bold text-text-primary">
                    {item.category}
                  </h3>
                  <div className="text-right">
                    <div className="text-sm text-text-secondary mb-1">Typical Hidden Costs</div>
                    <div className="text-2xl font-bold text-destructive">{item.savingsRange}</div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-destructive mb-3 flex items-center space-x-2">
                      <Icon name="AlertTriangle" size={16} />
                      <span>Hidden Costs Others Face:</span>
                    </h4>
                    <ul className="space-y-2">
                      {item.hiddenCosts.map((cost, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                          <Icon name="X" size={16} className="text-destructive mt-0.5 flex-shrink-0" />
                          <span>{cost}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-success mb-3 flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} />
                      <span>Bootminds Approach:</span>
                    </h4>
                    <div className="bg-success/10 rounded-lg p-4">
                      <p className="text-text-primary font-medium">{item.bootmindsApproach}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="card-elevated rounded-xl p-8 bg-gradient-to-br from-green-600/10 to-blue-600/10">
              <div className="text-center">
                <Icon name="Award" size={48} className="text-success mx-auto mb-4" />
                <h3 className="text-2xl font-headline font-bold text-text-primary mb-4">
                  Total Hidden Cost Elimination
                </h3>
                <p className="text-3xl font-bold text-success mb-2">Hidden costs eliminated</p>
                <p className="text-text-secondary mb-6">
                  Transparent, fixed-rate pricing replaces the typical change-order creep on India GCC setups
                </p>
                <Button variant="default" size="lg" iconName="Calculator" iconPosition="left">
                  Calculate Your Savings
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4">
            <Button
              variant="default"
              size="lg"
              className="bg-green-600 hover:bg-green-700"
              iconName="FileText"
              iconPosition="left"
            >
              Request Detailed Pricing
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calculator"
              iconPosition="left"
            >
              Build Custom Quote
            </Button>
            <Button
              variant="ghost"
              size="lg"
              iconName="Phone"
              iconPosition="left"
            >
              Speak to CFO Advisor
            </Button>
          </div>
          <p className="text-sm text-text-secondary mt-4">
            100% transparent pricing | No hidden fees | Fixed-rate options available
          </p>
        </div>
      </div>
    </section>
  );
};

export default TransparentEconomics;
